import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import {
  CheckCircle,
  Shield,
  ClipboardCheck,
  Truck,
  Factory,
  Package,
  ScanLine,
  Building2,
  ArrowRight,
  Star,
  Leaf,
  Microscope,
  FileSearch
} from 'lucide-react';
import LeadCaptureModal from '../LeadCaptureModal';
import MarketingHeroLottie from './MarketingHeroLottie';

/**
 * Homepage nova (pré-lançamento): gestão de qualidade ponta a ponta — indústria de alimentos.
 * Homepage principal do site (rota pública: /).
 */
const MarketingHomePageNew = () => {
  const [showLeadModal, setShowLeadModal] = useState(false);

  const jornadaEtapas = [
    {
      passo: '1',
      titulo: 'Recepção de matéria-prima',
      texto:
        'Registro de lotes do fornecedor, parâmetros de qualidade na entrada e decisão documentada de aceite ou rejeição.',
      Icon: Truck
    },
    {
      passo: '2',
      titulo: 'Rastreio no estoque',
      texto:
        'Saldo por recebimento e lote, pronto para auditoria e para consumo posterior na produção.',
      Icon: Package
    },
    {
      passo: '3',
      titulo: 'Ordens de produção',
      texto:
        'Planejamento por bateladas, consumo de MP por lote e execução do processo com validações por etapa.',
      Icon: Factory
    },
    {
      passo: '4',
      titulo: 'Produto acabado',
      texto:
        'Lote de fabricação identificado, status da ordem e encerramento com histórico para não conformidades.',
      Icon: ClipboardCheck
    },
    {
      passo: '5',
      titulo: 'Rastreabilidade',
      texto:
        'Consulta bidirecional: do lote do fornecedor ao lote fabricado, e do lote fabricado às MPs utilizadas.',
      Icon: ScanLine
    }
  ];

  return (
    <div className="bg-white">
      {/* Hero */}
      <section className="bg-gradient-to-br from-blue-50 via-white to-blue-50 py-14 md:py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16 items-center">
            <div>
              <p className="text-sm font-semibold uppercase tracking-wide text-blue-800 mb-3">
                Indústria de alimentos
              </p>
              <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-gray-900 mb-6 leading-tight">
                Qualidade e rastreabilidade da{' '}
                <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-700 to-cyan-500">
                  recepção ao lote pronto
                </span>
              </h1>
              <p className="text-lg md:text-xl text-gray-600 mb-8 max-w-xl">
                O QualiManager digitaliza a gestão de qualidade na fábrica: recebimento com critérios técnicos,
                produção com consumo por lote e rastreio completo — substituindo planilhas e papel em auditorias e
                recall.
              </p>
              <div className="flex flex-col sm:flex-row gap-4">
                <button
                  type="button"
                  onClick={() => setShowLeadModal(true)}
                  className="bg-gradient-to-r from-blue-700 to-cyan-500 text-white px-8 py-4 rounded-lg font-semibold hover:from-blue-800 hover:to-cyan-600 transition-all duration-200 flex items-center justify-center gap-2"
                >
                  <span>Falar com a equipe</span>
                  <ArrowRight className="h-5 w-5" />
                </button>
                <Link
                  to="/app/login"
                  className="border-2 border-gray-300 text-gray-700 px-8 py-4 rounded-lg font-semibold hover:border-orange-500 hover:text-blue-800 transition-all duration-200 inline-flex items-center justify-center"
                >
                  Já sou cliente — Entrar
                </Link>
              </div>
              <LeadCaptureModal isOpen={showLeadModal} onClose={() => setShowLeadModal(false)} />
            </div>
            <div className="flex justify-center lg:justify-end">
              <MarketingHeroLottie />
            </div>
          </div>
        </div>
      </section>

      {/* Pilares */}
      <section className="py-14 bg-white border-y border-gray-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8 text-center">
            <div>
              <div className="text-3xl font-bold text-blue-700 mb-2">Ponta a ponta</div>
              <div className="text-gray-600 text-sm">Da portaria à expedição do lote</div>
            </div>
            <div>
              <div className="text-3xl font-bold text-blue-600 mb-2">Por lote</div>
              <div className="text-gray-600 text-sm">MP e produto acabado rastreados</div>
            </div>
            <div>
              <div className="text-3xl font-bold text-violet-600 mb-2">BPF-ready</div>
              <div className="text-gray-600 text-sm">Registros para boas práticas</div>
            </div>
            <div>
              <div className="text-3xl font-bold text-orange-500 mb-2">Em nuvem</div>
              <div className="text-gray-600 text-sm">Acesso seguro e histórico</div>
            </div>
          </div>
        </div>
      </section>

      {/* Jornada — ids próprios; nav antiga não aponta aqui, mas útil para links internos */}
      <section id="jornada-qualidade" className="py-20 bg-slate-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16 max-w-3xl mx-auto">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Um fluxo único de qualidade na fábrica
            </h2>
            <p className="text-xl text-gray-600">
              Cada etapa gera evidência: quem decidiu, com qual lote e com quais parâmetros — base para auditoria e
              melhoria contínua.
            </p>
          </div>
          <div className="space-y-6 max-w-4xl mx-auto">
            {jornadaEtapas.map(({ passo, titulo, texto, Icon }) => (
              <div
                key={passo}
                className="flex gap-4 md:gap-6 bg-white rounded-xl border border-gray-200 p-5 md:p-6 shadow-sm"
              >
                <div className="flex-shrink-0 w-12 h-12 rounded-full bg-gradient-to-br from-blue-600 to-cyan-500 text-white font-bold flex items-center justify-center text-lg">
                  {passo}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-2">
                    <Icon className="h-5 w-5 text-blue-700 flex-shrink-0" />
                    <h3 className="text-lg font-semibold text-gray-900">{titulo}</h3>
                  </div>
                  <p className="text-gray-600">{texto}</p>
                </div>
                <CheckCircle className="h-6 w-6 text-cyan-600 flex-shrink-0 hidden sm:block" aria-hidden />
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Para empresas — id compatível com navbar */}
      <section id="para-empresas" className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">Para a indústria de alimentos</h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Menos retrabalho na qualidade, mais segurança nos dados de lote e fornecedor — integrado ao que você já
              faz com fornecedores no QualiManager.
            </p>
          </div>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-start">
            <div className="space-y-8">
              <div className="flex items-start gap-4">
                <div className="bg-blue-100 p-3 rounded-lg">
                  <Microscope className="h-6 w-6 text-blue-800" />
                </div>
                <div>
                  <h3 className="text-xl font-semibold text-gray-900 mb-2">Qualidade na entrada</h3>
                  <p className="text-gray-600">
                    Coleta de parâmetros no recebimento, análise de conformidade e decisão formal — tudo vinculado ao
                    lote do fornecedor.
                  </p>
                </div>
              </div>
              <div className="flex items-start gap-4">
                <div className="bg-blue-100 p-3 rounded-lg">
                  <Factory className="h-6 w-6 text-blue-700" />
                </div>
                <div>
                  <h3 className="text-xl font-semibold text-gray-900 mb-2">Produção com evidências</h3>
                  <p className="text-gray-600">
                    Ordens por bateladas, consumo de matéria-prima rastreado e etapas do processo com registros de
                    validação.
                  </p>
                </div>
              </div>
              <div className="flex items-start gap-4">
                <div className="bg-violet-100 p-3 rounded-lg">
                  <FileSearch className="h-6 w-6 text-violet-700" />
                </div>
                <div>
                  <h3 className="text-xl font-semibold text-gray-900 mb-2">Rastreabilidade bidirecional</h3>
                  <p className="text-gray-600">
                    Saiba onde cada lote de MP foi parar e de quais fornecimentos saiu cada lote fabricado — em poucos
                    cliques.
                  </p>
                </div>
              </div>
            </div>
            <div className="bg-gradient-to-br from-slate-50 to-blue-50 p-8 rounded-2xl border border-gray-200">
              <Building2 className="h-14 w-14 text-blue-700 mx-auto mb-4" />
              <h3 className="text-2xl font-bold text-gray-900 text-center mb-6">Visão da operação</h3>
              <ul className="space-y-4 text-gray-700">
                <li className="flex items-center gap-3">
                  <CheckCircle className="h-5 w-5 text-blue-700 flex-shrink-0" />
                  Recebimentos e liberações centralizados
                </li>
                <li className="flex items-center gap-3">
                  <CheckCircle className="h-5 w-5 text-blue-700 flex-shrink-0" />
                  Ordens de produção e status em tempo real
                </li>
                <li className="flex items-center gap-3">
                  <CheckCircle className="h-5 w-5 text-blue-700 flex-shrink-0" />
                  Fornecedores e documentação alinhados à compra
                </li>
                <li className="flex items-center gap-3">
                  <CheckCircle className="h-5 w-5 text-blue-700 flex-shrink-0" />
                  Base para BPF, auditorias e treinamento
                </li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Para fornecedores */}
      <section id="para-fornecedores" className="py-20 bg-blue-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">Para fornecedores</h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Continuamos conectando fornecedores homologados às indústrias: documentação, propostas e visibilidade no
              ecossistema QualiManager.
            </p>
          </div>
          <div className="flex flex-col md:flex-row gap-8 items-center justify-center">
            <div className="flex items-start gap-4 max-w-md">
              <Leaf className="h-10 w-10 text-blue-700 flex-shrink-0" />
              <div>
                <h3 className="font-semibold text-gray-900 mb-2">Cadastro e conformidade</h3>
                <p className="text-gray-600">
                  Mantenha certificações e documentos atualizados para ser encontrado por compradores que exigem
                  rastreio e qualidade.
                </p>
              </div>
            </div>
            <Link
              to="/cadastro-fornecedor"
              className="inline-flex items-center gap-2 bg-blue-700 text-white px-8 py-4 rounded-lg font-semibold hover:bg-blue-800 transition-colors"
            >
              Cadastro de fornecedor
              <ArrowRight className="h-5 w-5" />
            </Link>
          </div>
        </div>
      </section>

      {/* Marketplace */}
      <section id="marketplace" className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">Ecossistema B2B</h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto mb-10">
            Demandas, cotações e rede de fornecedores — integrados à mesma base de confiança que sustenta a qualidade na
            fábrica.
          </p>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 text-left">
            <div className="p-6 rounded-xl border border-gray-200 bg-gray-50">
              <Shield className="h-8 w-8 text-blue-600 mb-3" />
              <h3 className="font-bold text-gray-900 mb-2">Compradores</h3>
              <p className="text-gray-600 text-sm">
                Publique necessidades e receba propostas de parceiros com histórico documental.
              </p>
            </div>
            <div className="p-6 rounded-xl border border-gray-200 bg-gray-50">
              <Truck className="h-8 w-8 text-blue-700 mb-3" />
              <h3 className="font-bold text-gray-900 mb-2">Fornecedores</h3>
              <p className="text-gray-600 text-sm">
                Responda a oportunidades e fortaleça o relacionamento com indústrias auditáveis.
              </p>
            </div>
            <div className="p-6 rounded-xl border border-gray-200 bg-gray-50">
              <ScanLine className="h-8 w-8 text-violet-600 mb-3" />
              <h3 className="font-bold text-gray-900 mb-2">Rastreio</h3>
              <p className="text-gray-600 text-sm">
                Da cotação ao recebimento: mesma lógica de lote e responsabilidade na cadeia.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Depoimentos */}
      <section className="py-20 bg-slate-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold text-gray-900 text-center mb-12">Confiança na operação</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {[
              {
                quote:
                  'Hoje fechamos o ciclo do lote: recebimento, produção e rastreio num só lugar. Auditoria ficou objetiva.',
                nome: 'Coordenação de Qualidade',
                cargo: 'Indústria de alimentos'
              },
              {
                quote:
                  'Menos papel na expedição da MP e mais clareza de qual lote entrou em cada batelada.',
                nome: 'Gestão de Produção',
                cargo: 'Fábrica multiproduto'
              },
              {
                quote:
                  'O fornecedor entende que precisamos de lote e laudo desde a entrada — o sistema obriga o padrão certo.',
                nome: 'Compras & Suprimentos',
                cargo: 'Empresa cliente QualiManager'
              }
            ].map((d, i) => (
              <div key={i} className="bg-white p-6 rounded-xl border border-gray-200 shadow-sm">
                <div className="flex mb-3">
                  {[...Array(5)].map((_, j) => (
                    <Star key={j} className="h-5 w-5 text-amber-400 fill-amber-400" />
                  ))}
                </div>
                <p className="text-gray-700 mb-4">&ldquo;{d.quote}&rdquo;</p>
                <div className="font-semibold text-gray-900">{d.nome}</div>
                <div className="text-gray-500 text-sm">{d.cargo}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 bg-gradient-to-r from-blue-900 to-blue-700">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
            Pronto para evoluir a qualidade na sua fábrica?
          </h2>
          <p className="text-xl text-blue-100 mb-8 max-w-2xl mx-auto">
            Agende uma conversa ou acesse a plataforma se você já é cliente.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <button
              type="button"
              onClick={() => setShowLeadModal(true)}
              className="bg-white text-blue-900 px-8 py-4 rounded-lg font-semibold hover:bg-orange-50 transition-colors inline-flex items-center justify-center gap-2"
            >
              Quero conhecer
              <ArrowRight className="h-5 w-5" />
            </button>
            <Link
              to="/app/login"
              className="border-2 border-white text-white px-8 py-4 rounded-lg font-semibold hover:bg-white/10 transition-colors inline-flex items-center justify-center"
            >
              Entrar na plataforma
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default MarketingHomePageNew;
